// src/data/commandIndexClient.js
//
// B3-PORTFOLIO-COMMAND-TILE: Client functions for the Portfolio Command index.
// Mirrors the apiFetch + error pattern from pulseClient.js.
// Both endpoints use credentials: "include" and surface errors — no silent {}.

import { NetworkError, errorFromResponse } from "./errors.js";
import { log } from "../utils/logger.js";

async function apiFetch(url, init) {
  let res;
  try {
    res = await fetch(url, {
      ...init,
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    });
  } catch (err) {
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("commandIndexClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

/**
 * getCommandIndex()
 * GET /api/command-index
 * Returns: { composite: number|null, band: string|null,
 *   pillars: Array<{ key, label, score, status, band, weightUsed, breakdown }>,
 *   opportunities: Array<{ measure, measureBand, action, meta, liftPoints, effort, navTarget }> }
 * breakdown for data_trust: { checks: Array<{ id, passing, applicable, rate }> }
 */
export async function getCommandIndex() {
  return apiFetch("/api/command-index");
}

/**
 * Fetch the configurable Portfolio Command pillar list.
 * @returns {Promise<Array<{key: string, label: string, status: string, weight: number}>>}
 */
export async function getCommandIndexPillars() {
  return apiFetch("/api/command-index/pillars");
}

/**
 * Replace the Portfolio Command pillar configuration.
 * @param {Array<{key: string, status: string, weight: number}>} pillars
 */
export async function putCommandIndexPillars(pillars) {
  return apiFetch("/api/command-index/pillars", {
    method: "PUT",
    body: JSON.stringify({ pillars }),
  });
}

/**
 * getCommandIndexHistory()
 * GET /api/command-index/history
 * Returns: Array<{ id, periodEnd, cadence, composite, band, breakdown, createdAt }>
 * Sorted descending by periodEnd on the server; consumer should reverse for chart display.
 */
export async function getCommandIndexHistory() {
  return apiFetch("/api/command-index/history");
}

/**
 * getPortfolioCommandImages()
 * GET /api/pulse/category-images?surface=portfolio_command
 * Returns a canonical pillar-key → active image URL map. A read failure is
 * degraded to an empty map so the tile keeps its neutral visual fallback.
 */
export async function getPortfolioCommandImages() {
  try {
    return await apiFetch("/api/pulse/category-images?surface=portfolio_command");
  } catch {
    return {};
  }
}

/**
 * getDataTrustOpenRun()
 * GET /api/workflows/runs/open?sourceType=command_index&sourceId=data_trust
 * Returns: { id, name, status, startedAt, createdAt } | null
 * Used by DataTrustDrilldown and PortfolioCommandTile to show the dedup chip.
 */
export async function getDataTrustOpenRun() {
  return apiFetch("/api/workflows/runs/open?sourceType=command_index&sourceId=data_trust");
}

/**
 * Fetch the shared six-pillar Data Trust configuration.
 * @returns {Promise<{ pillars: Array }>}
 */
export async function getDataTrustWeights() {
  return apiFetch("/api/admin/data-trust/weights");
}

/**
 * Replace the complete shared Data Trust configuration.
 * @param {Array<{key: string, weight: number, isActive: boolean}>} pillars
 */
export async function putDataTrustWeights(pillars) {
  return apiFetch("/api/admin/data-trust/weights", {
    method: "PUT",
    body: JSON.stringify({ pillars }),
  });
}

export async function getDataTrustThreshold() {
  return apiFetch("/api/admin/data-trust/weights/threshold");
}

export async function putDataTrustThreshold(threshold) {
  return apiFetch("/api/admin/data-trust/weights/threshold", {
    method: "PUT", body: JSON.stringify({ threshold }),
  });
}

export async function getDataTrustSourceConfidence() {
  return apiFetch("/api/data-trust/source-confidence");
}

export async function putDataTrustSourceConfidence(pillarKey, value, rationale) {
  return apiFetch(`/api/data-trust/source-confidence/${encodeURIComponent(pillarKey)}`, {
    method: "PUT",
    body: JSON.stringify({ value, rationale }),
  });
}
